import { Navigate, Outlet, useLocation, type RouteObject } from "react-router-dom"
import { AuthRoute } from "./AuthRoute"
import { AppShell } from "./AppShell"
import { RequireAuth } from "./RequireAuth"
import { GuestOrAuth } from "./GuestOrAuth"
import { useCurrentUser } from "@/features/auth/hooks/useCurrentUser"
import { HomeView } from "@/features/home/HomeView"
import { VaultView } from "@/features/vault/VaultView"
import { QAView } from "@/features/qa/QAView"
import Landing from "@/features/nira2/landing/Landing"
import AnswerPage from "@/features/nira2/answer/AnswerPage"
import SharedPage from "@/features/nira2/shared/SharedPage"
import CasePage, { CasesPage } from "@/features/nira2/case/CasePage"
import WorkspaceAbout from "@/features/nira2/case/WorkspaceAbout"
import ConfirmPage from "@/features/nira2/case/ConfirmPage"
import ReadingPage from "@/features/nira2/case/ReadingPage"
import SiteHome from "@/features/nira2/site/SiteHome"
import SiteEthos from "@/features/nira2/site/SiteEthos"
import SitePricing from "@/features/nira2/site/SitePricing"
import SiteDocuments from "@/features/nira2/site/SiteDocuments"
import SitePrivacy from "@/features/nira2/site/SitePrivacy"
import SiteCoverage from "@/features/nira2/site/SiteCoverage"
import SimStart from "@/features/nira2/sim/SimStart"
import OioSimDecisions from "@/features/oio-sim/Decisions"
import OioSimDraft from "@/features/oio-sim/DraftPage"
import OiaSimDecisions from "@/features/oia-sim/Decisions"
import OiaSimDraft from "@/features/oia-sim/DraftPage"
import { NiraFullScreen } from "./NiraFullScreen"
import { SubscriptionPage } from "@/features/billing/pages/SubscriptionPage"
import { AccountPage, SubscriptionView } from "@/features/nira2/account/AccountPage"

/** Billing routes exist only when the deployment has billing on. */
function BillingRouteGuard() {
  const { me, isLoading } = useCurrentUser()
  if (isLoading) return null
  if (!me?.billing_enabled) return <Navigate to="/ask" replace />
  return <Outlet />
}

/** The old /about address of the site's home: to "/", keeping any #section. */
function AboutRedirect() {
  const { hash } = useLocation()
  return <Navigate to={{ pathname: "/", hash }} replace />
}

export const routeConfig: RouteObject[] = [
  {
    element: <NiraFullScreen />,
    children: [
      // Clerk's sign-in and sign-up URLs: they finish a Google sign-in or open the sign-in dialog.
      { path: "/auth/login/*", element: <AuthRoute mode="in" /> },
      { path: "/auth/signup/*", element: <AuthRoute mode="up" /> },
      // A shared answer opens without signing in: the link carries a masked, frozen copy.
      { path: "m/:token", element: <SharedPage /> },
      // Nira's public site (Site-Home, Site-Ethos): open to everyone, signed in or not.
      { index: true, element: <SiteHome /> },
      { path: "about", element: <AboutRedirect /> },
      { path: "how-we-work", element: <SiteEthos /> },
      { path: "pricing", element: <SitePricing /> },
      { path: "your-documents", element: <SiteDocuments /> },
      { path: "privacy", element: <SitePrivacy /> },
      { path: "coverage", element: <SiteCoverage /> },
    ],
  },
  {
    element: <GuestOrAuth />,
    children: [
      {
        element: <NiraFullScreen />,
        children: [
          // The research composer.
          { path: "ask", element: <Landing /> },
          { path: "nira", element: <Navigate to="/ask" replace /> },
          { path: "nira/:threadId", element: <AnswerPage /> },
          { path: "case/:caseId", element: <CasePage /> },
          { path: "case/:caseId/confirm", element: <ConfirmPage /> },
          { path: "case/:caseId/reading/:readingId", element: <ReadingPage /> },
          { path: "cases", element: <CasesPage /> },
          // Workspaces: the same pages under the new name. The old case-file paths above keep working.
          { path: "workspaces", element: <CasesPage /> },
          { path: "workspaces/about", element: <WorkspaceAbout /> },
          { path: "workspace/:caseId", element: <CasePage /> },
          { path: "workspace/:caseId/confirm", element: <ConfirmPage /> },
          { path: "workspace/:caseId/reading/:readingId", element: <ReadingPage /> },
          { path: "simulator", element: <SimStart /> },
          // One start page for both orders (handoff 4): the old landings send there.
          { path: "oio-sim", element: <Navigate to="/simulator" replace /> },
          { path: "oio-sim/:threadId", element: <OioSimDecisions /> },
          { path: "oio-sim/:threadId/draft", element: <OioSimDraft /> },
          { path: "oia-sim", element: <Navigate to="/simulator" replace /> },
          { path: "oia-sim/:threadId", element: <OiaSimDecisions /> },
          { path: "oia-sim/:threadId/draft", element: <OiaSimDraft /> },
        ],
      },
    ],
  },
  // The account, in Nira's pages (Profile and Subscription in the avatar menu): signed in only.
  // The subscription page is not behind BillingRouteGuard: where billing is off it says so.
  {
    element: <RequireAuth />,
    children: [
      {
        element: <NiraFullScreen />,
        children: [
          { path: "account", element: <AccountPage /> },
          { path: "account/subscription", element: <SubscriptionView /> },
        ],
      },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: "home", element: <HomeView /> },
          // /drafting is rendered persistently by AppShell (so the OIO stream
          // survives navigation); the routed slot here renders nothing.
          { path: "drafting", element: null },
          { path: "vault", element: <VaultView /> },
          { path: "qa", element: <QAView /> },
          {
            element: <BillingRouteGuard />,
            children: [
              { path: "subscription", element: <SubscriptionPage /> },
            ],
          },
        ],
      },
    ],
  },
  // An unknown or stale link lands on the research page instead of the router's error page.
  { path: "*", element: <Navigate to="/ask" replace /> },
]
